'use client';

import * as React from 'react';
import { Switch } from '@/components/ui/forms/meta/switch';

type AccessControlledSwitchProps = Omit<
  React.ComponentPropsWithoutRef<typeof Switch>,
  'checked' | 'defaultChecked' | 'onCheckedChange'
> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  requestAccess?: () => boolean | Promise<boolean>;
  animationDelayMs?: number;
};

function assignRef<T>(
  ref: React.ForwardedRef<T>,
  value: T | null
): void {
  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  if (ref) {
    ref.current = value;
  }
}

const DEFAULT_ANIMATION_DELAY_MS = 170;

const AccessControlledSwitch = React.forwardRef<
  HTMLButtonElement,
  AccessControlledSwitchProps
>(
  (
    {
      animationDelayMs = DEFAULT_ANIMATION_DELAY_MS,
      checked,
      disabled,
      onCheckedChange,
      requestAccess,
      ...props
    },
    ref
  ) => {
    const switchRef = React.useRef<HTMLButtonElement | null>(null);
    const [visualChecked, setVisualChecked] = React.useState(checked);
    const [isAwaitingAccess, setIsAwaitingAccess] = React.useState(false);

    React.useEffect(() => {
      if (!isAwaitingAccess) {
        setVisualChecked(checked);
      }
    }, [checked, isAwaitingAccess]);

    const finishEnableAttempt = React.useCallback(async () => {
      const allowed = requestAccess
        ? await Promise.resolve(requestAccess())
        : true;

      if (allowed) {
        onCheckedChange(true);
        setVisualChecked(true);
      } else {
        setVisualChecked(false);
      }

      setIsAwaitingAccess(false);
    }, [onCheckedChange, requestAccess]);

    React.useEffect(() => {
      if (!isAwaitingAccess || !visualChecked) {
        return;
      }

      let isSettled = false;

      const settle = () => {
        if (isSettled) {
          return;
        }

        isSettled = true;
        void finishEnableAttempt();
      };

      const timeoutId = window.setTimeout(settle, animationDelayMs);
      const node = switchRef.current;

      const handleTransitionEnd = (event: TransitionEvent) => {
        if (
          event.target !== node ||
          (event.propertyName !== 'background-color' &&
            event.propertyName !== 'background')
        ) {
          return;
        }

        window.clearTimeout(timeoutId);
        settle();
      };

      node?.addEventListener('transitionend', handleTransitionEnd);

      return () => {
        isSettled = true;
        window.clearTimeout(timeoutId);
        node?.removeEventListener('transitionend', handleTransitionEnd);
      };
    }, [animationDelayMs, finishEnableAttempt, isAwaitingAccess, visualChecked]);

    const handleCheckedChange = (nextChecked: boolean) => {
      if (disabled || isAwaitingAccess) {
        return;
      }

      if (!nextChecked) {
        setVisualChecked(false);
        setIsAwaitingAccess(false);
        onCheckedChange(false);
        return;
      }

      if (!requestAccess) {
        setVisualChecked(true);
        onCheckedChange(true);
        return;
      }

      setVisualChecked(true);
      setIsAwaitingAccess(true);
    };

    return (
      <Switch
        {...props}
        ref={(node) => {
          switchRef.current = node;
          assignRef(ref, node);
        }}
        checked={visualChecked}
        disabled={disabled}
        onCheckedChange={handleCheckedChange}
      />
    );
  }
);

AccessControlledSwitch.displayName = 'AccessControlledSwitch';

export { AccessControlledSwitch };
